import {
  Button,
  Flex,
  Icon,
  IconButton,
  Modal,
  ModalContent,
  ModalOverlay,
  Spinner,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { t } from "i18next";
import { Dispatch, KeyboardEvent, SetStateAction, useEffect, useRef, useState } from "react";
import { FaArrowUp } from "react-icons/fa";
import {
  AiModelDto,
  GptMessage,
  GPTRole,
  SignedDocumentDto,
  useSendAiChatMessageMutation,
  useStartDocumentChatMutation,
  ViewDocumentAsOwnerDto,
} from "../../autogen/bff-api";
import { isApiErrorDto } from "../errors/isApiErrorDto";
import { isFetchBaseQueryError } from "../errors/isFetchBaseQueryError";
import { useRumError } from "../errors/RumComponentContext/useRumError";
import { Logo } from "../Logo";

export const DocumentAnalysisModal = ({
  document,
  setDocumentForAnalysis,
}: {
  document: ViewDocumentAsOwnerDto | SignedDocumentDto;
  setDocumentForAnalysis: Dispatch<SetStateAction<ViewDocumentAsOwnerDto | SignedDocumentDto | undefined>>;
}) => {
  const [gptModel, setGptModel] = useState(AiModelDto.Gpt52);
  const [startChat, { isLoading: isStartingChat, error: startChatError }] = useStartDocumentChatMutation();
  const [sendMessage, { isLoading: isSendingMessage, error: sendMessageError }] = useSendAiChatMessageMutation();
  const rumError = useRumError();

  const [errorMsg, setErrorMsg] = useState<string>();
  const [newMessage, setNewMessage] = useState<string>("");
  const [messages, setMessages] = useState<GptMessage[]>();

  const chatRef = useRef<HTMLDivElement | null>(null);
  const msgRef = useRef<HTMLTextAreaElement | null>(null);
  const chatButtonRef = useRef<HTMLButtonElement | null>(null);

  const summaryPrompt = t(`Generate a short summary of the given text`);

  const scrollToBottom = () => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  };

  const handleClick = async (msg: string) => {
    if (!messages) await handleNewChat({ msg, model: AiModelDto.Gpt52 });
    else await handleMessage({ msg, model: gptModel, messages });
    setNewMessage("");
    msgRef.current?.focus();
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleClick(newMessage);
    }
  };

  const handleNewChat = async ({
    msg,
    model,
    messages,
  }: {
    msg: string;
    model: AiModelDto;
    messages?: GptMessage[];
  }) => {
    const updatedMessages: GptMessage[] = [...(messages ?? []), { role: GPTRole.User, content: msg }];
    setMessages(updatedMessages);
    await startChat({
      startDocumentChatRequest: {
        documentId: document.id,
        model,
        messages: updatedMessages,
        reasoningEffort: "none",
      },
    })
      .unwrap()
      .then((data) => {
        setMessages(data.messages);
      })
      .catch((e) => {
        if (e.data?.errorCode === "zz_DocumentApiErrorCode_RateLimitExceeded" && gptModel === AiModelDto.Gpt52) {
          setGptModel(AiModelDto.Gpt5Nano);
          handleNewChat({
            msg,
            model: AiModelDto.Gpt5Nano,
            messages: [
              ...(updatedMessages ?? []),
              { role: GPTRole.Assistant, content: t(e.data.errorCode) },
              { role: GPTRole.Assistant, content: `${t("Retrying with model")}: ${AiModelDto.Gpt5Nano}` },
            ],
          });
        }
        rumError({ message: e }, undefined);
      });
  };

  const handleMessage = async ({
    msg,
    model,
    messages,
  }: {
    msg: string;
    model: AiModelDto;
    messages: GptMessage[];
  }) => {
    if (!messages?.length) throw Error("Unexpected empty chat");
    const updatedMessages: GptMessage[] = [...messages, { role: GPTRole.User, content: msg }];
    setMessages(updatedMessages);
    await sendMessage({
      aiChatRequest: {
        model,
        messages: updatedMessages,
        reasoningEffort: "none",
      },
    })
      .unwrap()
      .then((data) => {
        setMessages([...updatedMessages, data.messages[0]]);
      })
      .catch((e) => {
        if (e.data?.errorCode === "zz_DocumentApiErrorCode_RateLimitExceeded" && gptModel === AiModelDto.Gpt52) {
          setGptModel(AiModelDto.Gpt5Nano);
          handleMessage({
            msg,
            model: AiModelDto.Gpt5Nano,
            messages: [
              ...(updatedMessages ?? []),
              { role: GPTRole.Assistant, content: t(e.data.errorCode) },
              { role: GPTRole.Assistant, content: `${t("Retrying with model")}: ${AiModelDto.Gpt5Nano}` },
            ],
          });
        }
        rumError({ message: e }, undefined);
      });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, errorMsg]);

  useEffect(() => {
    if (sendMessageError) {
      if (isFetchBaseQueryError(sendMessageError) && isApiErrorDto(sendMessageError.data))
        setErrorMsg(t(sendMessageError.data.errorCode) ?? undefined);
    }
    if (startChatError) {
      if (isFetchBaseQueryError(startChatError) && isApiErrorDto(startChatError.data))
        setErrorMsg(t(startChatError.data.errorCode) ?? undefined);
    }
    if (!sendMessageError && !startChatError) setErrorMsg(undefined);
  }, [startChatError, sendMessageError]);

  return (
    <Modal size={"2xl"} isOpen={!!document} onClose={() => setDocumentForAnalysis(undefined)}>
      <ModalOverlay />
      <ModalContent maxHeight="80%" height="80%" p="5">
        <Flex height="full" flexDir="column" p="4" rounded="base" overflow="hidden">
          <Flex alignItems="center" pb="4">
            <Flex color="smPrimary" mr="1">
              <Logo />
            </Flex>
            {t("Chat regarding")}:
            <Text fontWeight="bold" pl="1">
              {document.name}
            </Text>
          </Flex>
          <Flex
            ref={chatRef}
            flexDirection="column"
            grow="1"
            overflowY="scroll"
            width="full"
            bgColor="smBackgroundSecondary"
            rounded="lg"
            justifyContent="space-between"
            p="1"
          >
            <Flex flexDirection="column">
              {messages
                ?.filter((m) => m.role !== "system")
                ?.map((m, i) => (
                  <Flex
                    key={i}
                    flexDirection="column"
                    alignItems={m.role === "user" ? "end" : "start"}
                    rowGap="1"
                    py="4"
                  >
                    {m.role !== "user" && (
                      <Flex color="smPrimary">
                        <Icon as={Logo} />
                      </Flex>
                    )}
                    <Text
                      whiteSpace="pre-line"
                      maxWidth="80%"
                      rounded="md"
                      backgroundColor={m.role === "user" ? "smPrimary" : "smBackgroundTertiary"}
                      color={m.role === "user" ? "smBackground" : ""}
                      p="4"
                    >
                      {m.content}
                    </Text>
                  </Flex>
                ))}
              {!isStartingChat && !isSendingMessage && errorMsg && (
                <Flex flexDirection="column" rowGap="1" py="4">
                  <Flex color="orange">
                    <Icon as={Logo} />
                  </Flex>
                  <Text
                    whiteSpace={"pre-line"}
                    maxWidth="80%"
                    rounded="md"
                    backgroundColor={"smBackgroundTertiary"}
                    color="orange"
                    p="4"
                  >
                    {t(errorMsg)}
                  </Text>
                </Flex>
              )}
            </Flex>
            <Flex justifyContent="space-between" alignItems="end">
              <Flex>
                {(isStartingChat || isSendingMessage) && <Spinner colorScheme="teal" minHeight="6" minWidth="6" />}
              </Flex>
              <Text fontSize="x-small" textAlign="right" color="smMuted">
                {t("Model")}: {gptModel}
              </Text>
            </Flex>
          </Flex>
          <Textarea
            ref={msgRef}
            autoFocus
            placeholder={`${t("Ask the AI something")}...`}
            name="new chat message"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => {
              handleKeyPress(e);
            }}
            mt="2"
          />
          <Flex width={"full"} justifyContent="space-between" flexDirection="row-reverse" pt="2">
            <IconButton
              ref={chatButtonRef}
              aria-label="ask ai"
              icon={<Icon as={FaArrowUp} />}
              colorScheme="purple"
              isDisabled={!newMessage}
              onClick={() => handleClick(newMessage)}
            />
            <Button
              variant="outline"
              colorScheme="teal"
              onClick={() => {
                setNewMessage(summaryPrompt);
                handleClick(summaryPrompt);
              }}
            >
              {t("Generate Summary")}...
            </Button>
          </Flex>
        </Flex>
      </ModalContent>
    </Modal>
  );
};
